import { useEffect } from "react";

import { ThemeConfig } from "../themes/types";

/**
 * Hook to dynamically load fonts from the theme
 * This hook will:
 * 1. Parse the font family string from the theme
 * 2. Load the required fonts from local files or Google Fonts
 * 3. Clean up previous font links when the theme changes
 */
export const useFontLoader = (theme: ThemeConfig): void => {
  useEffect(() => {
    // Skip if no theme or no font family is provided
    if (!theme?.typography?.fontFamily) return;

    // Parse font families (comma separated)
    const fontFamilies = theme.typography.fontFamily
      .split(",")
      .map((font) => font.trim())
      .filter((font) => {
        // Filter out generic font families
        const genericFonts = [
          "sans-serif",
          "serif",
          "monospace",
          "cursive",
          "fantasy",
          "system-ui",
        ];
        return !genericFonts.includes(font.toLowerCase());
      });

    // Create a unique ID for this theme's font links
    const themeId = `font-loader-${theme.name}`;

    // Remove any previous font loaders with this theme ID
    document
      .querySelectorAll(`[data-font-loader="${themeId}"]`)
      .forEach((element) => {
        element.remove();
      });

    // Function to load local Cera Round Pro fonts for Skazka theme
    const loadLocalCeraRoundPro = (): void => {
      const fontFaces = `
        @font-face {
          font-family: 'Cera Round Pro';
          font-weight: 300;
          font-style: normal;
          src: url('/fonts/Cera-round/TypeMates - CeraRoundPro-Light.otf') format('opentype');
          font-display: swap;
        }
        @font-face {
          font-family: 'Cera Round Pro';
          font-weight: 400;
          font-style: normal;
          src: url('/fonts/Cera-round/TypeMates - CeraRoundPro-Regular.otf') format('opentype');
          font-display: swap;
        }
        @font-face {
          font-family: 'Cera Round Pro';
          font-weight: 500;
          font-style: normal;
          src: url('/fonts/Cera-round/TypeMates - CeraRoundPro-Medium.otf') format('opentype');
          font-display: swap;
        }
        @font-face {
          font-family: 'Cera Round Pro';
          font-weight: 700;
          font-style: normal;
          src: url('/fonts/Cera-round/TypeMates - CeraRoundPro-Bold.otf') format('opentype');
          font-display: swap;
        }
        @font-face {
          font-family: 'Cera Round Pro';
          font-weight: 900;
          font-style: normal;
          src: url('/fonts/Cera-round/TypeMates - CeraRoundPro-Black.otf') format('opentype');
          font-display: swap;
        }
      `;

      const style = document.createElement("style");
      style.textContent = fontFaces;
      style.setAttribute("data-font-loader", themeId);
      document.head.appendChild(style);
    };

    // If it's the Skazka theme and has Cera Round Pro, use local fonts
    if (
      theme.name === "Skazka" &&
      fontFamilies.some((font) => font.toLowerCase().includes("cera round pro"))
    ) {
      loadLocalCeraRoundPro();
      // Return early as we don't need to load from Google Fonts
      return;
    }

    // Function to create Google Fonts link for a font family
    const createGoogleFontLink = (
      fontFamily: string
    ): HTMLLinkElement | null => {
      // Skip font loading if it's not likely to be a Google Font
      if (fontFamily.includes(".") || fontFamily.startsWith("var(")) {
        return null;
      }

      // Font weights to load (from the most common ones)
      // In a real implementation, you might want to analyze the font-weight usage in your app
      const weights = ["400", "600", "700"];

      // Normalize the font name for the URL (spaces to +)
      const fontUrl = fontFamily.replace(/\s+/g, "+");

      // Create preconnect links for Google Fonts
      const preconnectGoogle = document.createElement("link");
      preconnectGoogle.rel = "preconnect";
      preconnectGoogle.href = "https://fonts.googleapis.com";
      preconnectGoogle.setAttribute("data-font-loader", themeId);

      const preconnectGstatic = document.createElement("link");
      preconnectGstatic.rel = "preconnect";
      preconnectGstatic.href = "https://fonts.gstatic.com";
      preconnectGstatic.crossOrigin = "anonymous";
      preconnectGstatic.setAttribute("data-font-loader", themeId);

      // Add preconnect links to document head
      document.head.appendChild(preconnectGoogle);
      document.head.appendChild(preconnectGstatic);

      // Create the main font link
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = `https://fonts.googleapis.com/css2?family=${fontUrl}:wght@${weights.join(
        ";"
      )}&display=swap`;
      link.setAttribute("data-font-loader", themeId);

      return link;
    };

    // Load each font family from Google Fonts (only if not already using local fonts)
    fontFamilies.forEach((fontFamily) => {
      const link = createGoogleFontLink(fontFamily);
      if (link) {
        document.head.appendChild(link);
      }
    });

    // Cleanup function to remove links when component unmounts or theme changes
    return () => {
      document
        .querySelectorAll(`[data-font-loader="${themeId}"]`)
        .forEach((element) => {
          element.remove();
        });
    };
  }, [theme]); // Re-run effect when theme changes
};
